// ---------------------------------------------------------
// Datei: hubspotMapper.ts
// Zweck: Mapper-Funktionen für HubSpot Deal-Daten
// Besonderheiten:
// - Konvertiert API-Response in UI-freundliche Struktur
// - Unterstützt Multi-Files (FileIDs getrennt durch Semikolon)
// - Robustes Parsing mit Fallbacks
// ---------------------------------------------------------

import { HubSpotDeal, DealDocument } from '../types/hubspot';
import { HUBSPOT_DOCUMENT_PROPERTIES, DOCUMENT_PROPERTY_NAMES } from '../constants/hubspotDocuments';

// === FileID-Parser ===
// Wandelt einen FileID-String in ein Array um.
// Unterstützt drei Fälle:
// 1. null/undefined → []
// 2. Einzelne FileID → ["123456"]
// 3. Multi-Files → ["123456", "789012"]
//
// Warum Semikolon als Trennzeichen?
// - HubSpot speichert Multi-Files als String mit Semikolon
// - Beispiel: "123456;789012;345678"
export function parseFileIds(value: string | null | undefined): string[] {
  // Fall 1: Kein Wert vorhanden
  if (!value) {
    return [];
  }

  // Fall 2 & 3: String in Array splitten
  // - trim() entfernt Whitespace
  // - split(';') trennt am Semikolon
  // - filter(id => id) entfernt leere Strings
  return value
    .split(';')
    .map(id => id.trim())
    .filter(id => id.length > 0);
}

// === Label-Mapper ===
// Holt den deutschen Label für einen Property-Namen.
// Fallback: Property-Name selbst (für unbekannte Properties)
//
// Warum ein Fallback?
// - Robustheit: Auch wenn neue Properties hinzukommen, bricht die UI nicht
// - Debugging: Unbekannte Properties werden sichtbar
export function getDocumentLabel(propertyName: string): string {
  const metadata = HUBSPOT_DOCUMENT_PROPERTIES[propertyName];
  return metadata?.label || propertyName;
}

// === Deal → Documents Mapper ===
// Hauptfunktion: Wandelt einen HubSpot Deal in eine Liste von DealDocuments um.
// Durchläuft alle konfigurierten Dokument-Properties und extrahiert ihre Werte.
//
// Warum diese Normalisierung?
// - Die UI arbeitet mit einer einheitlichen DealDocument-Struktur
// - Einfacheres Handling von Multi-Files
// - Klare Trennung von API-Format und UI-Format
export function mapDealToDocuments(deal: HubSpotDeal): DealDocument[] {
  const documents: DealDocument[] = [];

  // Iteriere über alle konfigurierten Dokument-Properties
  for (const propertyName of DOCUMENT_PROPERTY_NAMES) {
    const metadata = HUBSPOT_DOCUMENT_PROPERTIES[propertyName];

    // Wert aus dem Deal extrahieren (Type-Safe mit 'as any' wegen dynamischer Keys)
    const rawValue = (deal as any)[propertyName] as string | null | undefined;

    // FileIDs parsen ([] wenn null)
    const fileIds = parseFileIds(rawValue);

    // Status berechnen: present wenn mindestens eine FileID vorhanden
    const status: 'missing' | 'present' = fileIds.length > 0 ? 'present' : 'missing';

    // DealDocument erstellen
    documents.push({
      propertyName,
      label: metadata.label,
      fileIds,
      status,
      required: metadata.required,
      phase: metadata.phase
    });
  }

  return documents;
}

// === Deal-Zusammenfassung berechnen ===
// Berechnet Statistiken für einen Deal (z.B. "3 von 6 Pflichtdokumenten fehlen")
//
// Return-Wert:
// {
//   total: Anzahl aller Dokumente,
//   present: Anzahl vorhandener Dokumente,
//   missing: Anzahl fehlender Dokumente,
//   requiredMissing: Anzahl fehlender Pflichtdokumente
// }
export function getDealSummary(documents: DealDocument[]) {
  const total = documents.length;
  const present = documents.filter(doc => doc.status === 'present').length;
  const missing = documents.filter(doc => doc.status === 'missing').length;
  const requiredMissing = documents.filter(
    doc => doc.required && doc.status === 'missing'
  ).length;

  return {
    total,
    present,
    missing,
    requiredMissing
  };
}
